<script lang="ts" setup>
import { ref, onMounted, watch, nextTick } from "vue";

// Dependencies
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, LegendComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { UniversalTransition } from "echarts/features";

// Config
import { getConfig, getColor, getThemeColor, getMaxValueLength } from "@/features/Charts/config/echarts";
import { readChartTokens } from "@/features/Charts/config/chartTokens";
import { useThemeChange } from "@/features/Charts/composables/useThemeChange";
import { getBarEndRadius } from "@/features/Charts/utils/barBorderRadius";

// Props
import { verticalBarChartProps } from "../props/echartProps";

// Register the required components
echarts.use([BarChart, GridComponent, UniversalTransition, SVGRenderer, LegendComponent]);

const props = defineProps({
  ...verticalBarChartProps,
});

const categories = ref<string[]>([]);
const eventsInitialized = ref<boolean>(false);
// Named so a theme flip can remove it before disposing the instance it closes over — an
// unremoved listener would call `resize()` on a disposed chart on every future window resize.
let resizeHandler: (() => void) | null = null;

const chartRef = ref<HTMLElement | null>(null);
const config = ref();

function preamble() {
  // Get config
  config.value = getConfig("VerticalBarChart", props.settings, props.data);

  // Register theme
  echarts.registerTheme(`${config.value.name}Theme`, config.value.theme);

  // Get categories
  categories.value = Object.values(props.data)[0].entries.map((entry) => entry.label);
}

function buildSeries() {
  const stackedSet = props.settings.stackedCategories?.length ? new Set(props.settings.stackedCategories) : null;
  const tokens = readChartTokens();

  const cursor = props.onClick ? "pointer" : "default";

  // ECharts applies barMinHeight per segment, not per bar, so in a stack it inflates a small segment
  // past its real length and gives a zero-valued one a stub that lands on top of its neighbour. A
  // stack's length is what carries the meaning, so drop the minimum wherever we stack (RTLCA-5189).
  const stackMinHeight = props.settings.stacked || stackedSet ? { barMinHeight: 0 } : {};

  const makeEntry = (entry: { value: number; id?: string }, index: number) => ({
    value: entry.value,
    id: entry.id,
    itemStyle: {
      // The config's fixed [4, 4, 0, 0] rounds the top, which is the end a *positive* column grows
      // towards. On a negative value it rounds the end sitting on the zero line and squares off the
      // free end at the bottom, so the column reads as if it pointed the wrong way (RTLCA-5189).
      borderRadius: getBarEndRadius(entry.value, "vertical"),
      ...(props.settings.legend
        ? {}
        : {
            color: getColor({
              index,
              value: entry.value,
              series: props.data,
            }),
          }),
    },
    ...props.eventMetadata(),
  });

  if (!stackedSet) {
    return Object.entries(props.data).map(([key, item], index) => ({
      ...config.value.series,
      ...stackMinHeight,
      realtimeSort: props.settings.realtimeSort,
      name: item.name ?? key,
      data: item.entries.map((entry) => makeEntry(entry, index)),
      stack: props.settings.stacked ? "stack" : undefined,
      cursor,
    }));
  }

  // Stacked-categories mode: legend shows materials, not stages.
  // - Main series per material: bars at every non-stacked (stage) position,
  //   side-by-side across materials, coloured by material.
  // - Breakdown series per (stage × material): only have a value at the
  //   stacked (Total) positions, all share the material's name so one legend
  //   entry toggles everything for that material.  Per-material stack groups
  //   keep the materials side-by-side while stages stack within each column.
  //   Each stage segment uses a shade of the material colour.
  const stageLabels = categories.value.filter((cat) => !stackedSet.has(cat));
  const materials = Object.entries(props.data);
  const activeStages = stageLabels.filter((label) =>
    materials.some(([, item]) => {
      const e = item.entries.find((e) => e.label === label);
      return e && e.value !== 0;
    })
  );
  const result: any[] = [];

  // 1. Main series per material — stage positions only
  materials.forEach(([key, item], matIndex) => {
    const name = item.name ?? key;
    const color = getThemeColor(matIndex);

    result.push({
      ...config.value.series,
      ...stackMinHeight,
      name,
      color,
      stack: `mat_${matIndex}`,
      data: categories.value.map((cat) => {
        if (stackedSet.has(cat)) return null;
        const entry = item.entries.find((e) => e.label === cat);
        if (!entry) return null;
        return { value: entry.value, id: entry.id, ...props.eventMetadata() };
      }),
      cursor,
    });
  });

  // 2. Breakdown series for stacked (Total) positions
  activeStages.forEach((stageLabel) => {
    materials.forEach(([key, item], matIndex) => {
      const name = item.name ?? key;
      const color = getThemeColor(matIndex);
      const stageEntry = item.entries.find((e) => e.label === stageLabel);

      result.push({
        ...config.value.series,
        ...stackMinHeight,
        name,
        stack: `mat_${matIndex}`,
        data: categories.value.map((cat) => {
          if (!stackedSet.has(cat) || !stageEntry) return null;
          return {
            value: stageEntry.value,
            id: stageEntry.id,
            itemStyle: {
              color,
              borderColor: tokens.hairlineBorder,
              borderWidth: 1,
            },
            ...props.eventMetadata(),
          };
        }),
        cursor,
      });
    });
  });

  return result;
}

function initChart() {
  const chart = echarts.getInstanceByDom(chartRef.value!) ?? echarts.init(chartRef.value!, `${config.value.name}Theme`);

  const maxLength = getMaxValueLength(props.data);
  const series = buildSeries();

  chart.setOption(
    {
      grid: {
        ...config.value.grid,
      },
      legend: {
        show: props.settings.legend,
        type: "scroll",
      },
      yAxis: {
        ...config.value.yAxis,
        name: props.unit,
        nameGap: Math.max(maxLength * 9, 50),
      },
      xAxis: {
        ...config.value.xAxis,
        data: categories.value,
        inverse: props.settings.inverse,
        axisLabel: {
          ...config.value.xAxis.axisLabel,
          rotate: props.settings.rotateLabels ? props.settings.rotateLabels : 0,
        },
      },
      series,
    },
    { notMerge: true }
  );

  // Initialize events if not already initialized
  if (!eventsInitialized.value) {
    chart.on("mouseover", (params) => {
      if (params.seriesType === "bar" && props.onHover) {
        props.onHover(params);
      }
    });

    chart.on("mouseout", (params) => {
      if (params.seriesType === "bar" && props.onLeave) {
        props.onLeave();
      }
    });

    chart.on("click", (params) => {
      if (props.onClick) {
        props.onClick(params);
      }
    });

    resizeHandler = () => chart.resize();
    window.addEventListener("resize", resizeHandler);

    eventsInitialized.value = true;
  }
}

onMounted(() => {
  nextTick(() => {
    if (props.data && Object.values(props.data).length > 0) {
      preamble();
      if (chartRef.value) {
        initChart();
      }
    }
  });
});

watch(
  () => [props.data, props.unit],
  () => {
    // Initialize the chart if possible
    if (props.data && Object.values(props.data).length > 0) {
      // Preamble
      preamble();

      if (chartRef.value) {
        // Initialize the chart
        initChart();
      }
    }
  },
  {
    deep: true,
  }
);

// Dispose and redraw so the registered theme (only read at init time) picks up fresh colours.
// Clear the resize listener first, and reset eventsInitialized so the new instance gets its own.
useThemeChange(() => {
  if (!chartRef.value || !(props.data && Object.values(props.data).length > 0)) return;
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
    resizeHandler = null;
  }
  echarts.getInstanceByDom(chartRef.value)?.dispose();
  eventsInitialized.value = false;
  preamble();
  initChart();
});
</script>
<template>
  <div
    ref="chartRef"
    class="h-full w-full" />
</template>
